import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Image as ImageIcon, ZoomIn, X } from 'lucide-react';
import { getGallery } from '../lib/api';

const GallerySectionImproved = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getGallery();
        setItems(data);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section id="gallery" className="bg-[#0A1628] py-24 md:py-32 relative overflow-hidden">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto px-6 relative z-10"
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="mb-8">
          <span className="text-[#60A5FA] text-lg font-mono">Nuestro portafolio</span>
        </motion.div>

        <motion.h2 variants={itemVariants} className="text-5xl md:text-6xl font-bold text-white mb-6">
          Galería de <span className="text-[#60A5FA]">Trabajos</span>
        </motion.h2>

        <motion.p variants={itemVariants} className="text-white/70 text-lg mb-12 max-w-2xl">
          Descubre la calidad de nuestros trabajos y transformaciones
        </motion.p>

        {loading ? (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="break-inside-avoid bg-[#0F1D33]/50 rounded-2xl animate-pulse" style={{ aspectRatio: [1, 1.2, 0.9][i % 3] }} />
            ))}
          </div>
        ) : items.length === 0 ? (
          // Elegant empty state
          <motion.div
            variants={itemVariants}
            className="bg-gradient-to-r from-[#60A5FA]/10 to-[#3B82F6]/10 border border-[#60A5FA]/30 rounded-3xl p-12 text-center backdrop-blur-sm"
          >
            <div className="w-20 h-20 bg-[#60A5FA]/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <ImageIcon className="w-10 h-10 text-[#60A5FA]" />
            </div>
            <h3 className="text-white font-semibold text-2xl mb-3">
              Próximamente: Fotos de nuestros trabajos
            </h3>
            <p className="text-white/60 max-w-xl mx-auto">
              Estamos preparando una galería con imágenes profesionales de nuestros servicios.
              ¡Pronto podrás ver todas nuestras transformaciones aquí!
            </p>
          </motion.div>
        ) : (
          // Dynamic masonry grid - grows automatically
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {items.map((item) => (
              <motion.div
                key={item.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedImage(item)}
                className="break-inside-avoid group relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#60A5FA]/50 transition-all duration-300 cursor-pointer"
              >
                <img
                  src={item.image_url}
                  alt={item.title || 'Trabajo Lavaporcar'}
                  loading="lazy"
                  className="w-full h-auto object-cover"
                />
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  {item.title && (
                    <p className="text-white font-semibold text-lg mb-1">{item.title}</p>
                  )}
                  <div className="flex items-center space-x-2">
                    <span className="text-[#60A5FA] text-sm capitalize">{item.category}</span>
                    <ZoomIn className="w-4 h-4 text-[#60A5FA]" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="relative max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
              />
              {selectedImage.title && (
                <p className="text-white text-center mt-4 text-lg">{selectedImage.title}</p>
              )}
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GallerySectionImproved;
